import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronDown } from 'lucide-react';
import RadarChart from '../components/Report/AnalysisCharts/RadarChart';
import ScoreBarChart from '../components/Report/AnalysisCharts/ScoreBarChart';
import ScoreHeatmap from '../components/Report/AnalysisCharts/ScoreHeatmap';
import AttributeTopBrands from '../components/Report/AnalysisCharts/AttributeTopBrands';
import JustificativasSection from '../components/Report/AnalysisCharts/JustificativasSection';
// Atributos analisados
const ATTRIBUTES = ['Inovação e Uso de Tecnologia', 'Custo x Benefício', 'Reputação e Credibilidade no Mercado', 'Comprovação de Resultados (Cases)', 'Transparência e Qualidade dos Relatórios', 'Qualidade do Atendimento e Suporte', 'Capacidade de Personalização da Estratégia', 'Amplitude do Portfólio de Serviços', 'Autoridade e Produção de Conteúdo Relevante', 'Especialização e Expertise em SEO'];
// Marcas com nova paleta de cores
const BRANDS = [{
  name: 'Conversion',
  color: '#1649ff',
  strokeWidth: 2
}, {
  name: 'Hedgehog Digital',
  color: '#334155',
  strokeWidth: 2
}, {
  name: 'Agência Mestre',
  color: '#4B5563',
  strokeWidth: 2
}, {
  name: 'Orgânica Digital',
  color: '#6B7280',
  strokeWidth: 2
}, {
  name: 'Agência Papoca',
  color: '#94A3B8',
  strokeWidth: 2
}, {
  name: 'LiveSEO',
  color: '#A1A1AA',
  strokeWidth: 2
} // Cinza 5
];
// URLs das marcas para favicon
const BRAND_URLS: Record<string, string> = {
  Conversion: 'https://www.conversion.com.br',
  'Hedgehog Digital': 'https://br.hedgehogdigital.co.uk/',
  'Agência Mestre': 'https://www.agenciamestre.com',
  'Orgânica Digital': 'https://www.organicadigital.com',
  'Agência Papoca': 'https://agenciapapoca.com.br/',
  LiveSEO: 'https://liveseo.com.br'
};
// Gerar dados mockados para 8 semanas com variações moderadas
const generateMockData = () => {
  const weeks = [];
  const startDate = new Date('2024-01-01');
  // Inicializar scores base para cada marca
  const baseScores: any = {};
  BRANDS.forEach(brand => {
    baseScores[brand.name] = {
      geral: brand.name === 'Conversion' ? 88 : 75 + Math.random() * 12
    };
    ATTRIBUTES.forEach(attr => {
      baseScores[brand.name][attr] = brand.name === 'Conversion' ? 86 + Math.random() * 6 : 74 + Math.random() * 14;
    });
  });
  for (let week = 1; week <= 8; week++) {
    const weekDate = new Date(startDate);
    weekDate.setDate(startDate.getDate() + (week - 1) * 7);
    const weekLabel = `Semana ${week}`;
    const weekData: any = {
      week: weekLabel,
      date: weekDate.toISOString().split('T')[0]
    };
    // Gerar scores com variações moderadas (meio termo)
    BRANDS.forEach(brand => {
      // Score geral - variação moderada de ±3 pontos
      const variation = (Math.random() - 0.5) * 6;
      baseScores[brand.name].geral = Math.min(95, Math.max(70, baseScores[brand.name].geral + variation * 0.5));
      weekData[`${brand.name}_geral`] = baseScores[brand.name].geral;
      // Scores por atributo - variação moderada de ±3 pontos
      ATTRIBUTES.forEach(attr => {
        const attrVariation = (Math.random() - 0.5) * 6;
        baseScores[brand.name][attr] = Math.min(95, Math.max(70, baseScores[brand.name][attr] + attrVariation * 0.5));
        weekData[`${brand.name}_${attr}`] = baseScores[brand.name][attr];
      });
    });
    weeks.push(weekData);
  }
  return weeks;
};
// Calcular rankings baseados nos scores
const calculateRankings = (data: any[], metric: string) => {
  return data.map(week => {
    const scores = BRANDS.map(brand => ({
      brand: brand.name,
      score: week[`${brand.name}_${metric}`]
    }));
    // Ordenar por score (maior para menor)
    scores.sort((a, b) => b.score - a.score);
    // Atribuir posições
    const rankings: any = {
      week: week.week,
      date: week.date
    };
    scores.forEach((item, index) => {
      rankings[`${item.brand}_position`] = index + 1;
      rankings[`${item.brand}_score`] = item.score.toFixed(1);
    });
    return rankings;
  });
};
const TesteSerieTemporal = () => {
  const [selectedAttribute, setSelectedAttribute] = useState(ATTRIBUTES[0]);
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-02-19');
  const [hiddenBrands, setHiddenBrands] = useState<string[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  // Gerar dados mockados UMA ÚNICA VEZ usando useMemo
  const mockData = useMemo(() => generateMockData(), []);
  // Calcular rankings - recalcula apenas quando selectedAttribute ou mockData mudam
  const generalRankings = useMemo(() => calculateRankings(mockData, 'geral'), [mockData]);
  const attributeRankings = useMemo(() => calculateRankings(mockData, selectedAttribute), [mockData, selectedAttribute]);
  // Filtrar dados por período
  const filterByDate = (data: any[]) => {
    return data.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
    });
  };
  const filteredGeneralRankings = filterByDate(generalRankings);
  const filteredAttributeRankings = filterByDate(attributeRankings);
  // Toggle brand visibility
  const handleLegendClick = (brandName: string) => {
    setHiddenBrands(prev => prev.includes(brandName) ? prev.filter(b => b !== brandName) : [...prev, brandName]);
  };
  // Get data for selected week
  const getWeekData = () => {
    if (!selectedWeek) return null;
    const weekData = mockData.find(w => w.week === selectedWeek);
    if (!weekData) return null;
    // Prepare scores in the format expected by the components
    const scores: Record<string, Record<string, number>> = {};
    const scoreGeral: Record<string, number> = {};
    BRANDS.forEach(brand => {
      scores[brand.name] = {};
      ATTRIBUTES.forEach(attr => {
        scores[brand.name][attr] = weekData[`${brand.name}_${attr}`];
      });
      scoreGeral[brand.name] = weekData[`${brand.name}_geral`];
    });
    return {
      scores,
      scoreGeral
    };
  };
  const weekData = getWeekData();
  // Get favicon from URL
  const getFavicon = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=example.com&sz=128`;
    }
  };
  // Find competitor URL
  const findCompetitorUrl = (brandName: string) => {
    return BRAND_URLS[brandName] || '';
  };
  // Custom tooltip ordenado por posição
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Criar array com todas as marcas e suas posições
      const brandData = BRANDS.map(brand => ({
        name: brand.name,
        color: brand.color,
        position: data[`${brand.name}_position`],
        score: data[`${brand.name}_score`]
      }));
      // Ordenar por posição (1º lugar primeiro)
      brandData.sort((a, b) => a.position - b.position);
      return <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg" data-id="element-1378">
          <p className="font-semibold text-gray-900 mb-2" data-id="element-1379">{data.week}</p>
          {brandData.map((brand, index) => <div key={index} className="flex items-center gap-2 text-sm mb-1" data-id="element-1380">
              <div className="w-3 h-3 rounded-full" style={{
            backgroundColor: brand.color
          }} data-id="element-1381" />
              <span className="text-gray-900" data-id="element-1382">
                {brand.name}: {brand.score}
              </span>
            </div>)}
        </div>;
    }
    return null;
  };
  // Custom Legend com click handler e barra mais grossa
  const CustomLegend = ({
    payload
  }: any) => {
    return <div className="flex flex-wrap justify-center gap-4 pt-4" data-id="element-1383">
        {payload.map((entry: any, index: number) => <div key={index} onClick={() => handleLegendClick(entry.value)} className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-opacity" style={{
        opacity: hiddenBrands.includes(entry.value) ? 0.3 : 1
      }} data-id="element-1384">
            <div className="w-8 h-1" style={{
          backgroundColor: entry.color
        }} data-id="element-1385" />
            <span className="text-sm text-gray-700" data-id="element-1386">{entry.value}</span>
          </div>)}
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 py-8" data-id="element-1387">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-1388">
        {/* Header */}
        <div className="mb-8" data-id="element-1389">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1390">
            Análise Temporal de Branding Semântico
          </h1>
          <p className="text-gray-600" data-id="element-1391">
            Acompanhe a evolução do ranking das marcas ao longo do tempo
          </p>
        </div>

        {/* Filtro de Período */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8" data-id="element-1392">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1393">
            Filtro de Período
          </h2>
          <div className="flex flex-col md:flex-row gap-4" data-id="element-1394">
            <div className="w-full md:w-64" data-id="element-1395">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1396">
                Data Inicial
              </label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" data-id="element-1397" />
            </div>
            <div className="w-full md:w-64" data-id="element-1398">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1399">
                Data Final
              </label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" data-id="element-1400" />
            </div>
          </div>
        </div>

        {/* Seção 1: Evolução do Ranking Geral */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8" data-id="element-1401">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1402">
            Evolução do Ranking Geral
          </h2>
          <div className="w-full h-[500px]" data-id="element-1403">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1404">
              <LineChart data={filteredGeneralRankings} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60
            }} data-id="element-1405">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-1406" />
                <XAxis dataKey="week" angle={-45} textAnchor="end" height={80} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} data-id="element-1407" />
                <YAxis reversed domain={[1, 6]} ticks={[1, 2, 3, 4, 5, 6]} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} label={{
                value: 'Posição no Ranking',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#6b7280',
                  fontSize: 14
                }
              }} data-id="element-1408" />
                <Tooltip content={<CustomTooltip data-id="element-1410" />} data-id="element-1409" />
                <Legend content={<CustomLegend data-id="element-1412" />} data-id="element-1411" />
                {BRANDS.map(brand => <Line key={brand.name} type="monotone" dataKey={`${brand.name}_position`} name={brand.name} stroke={brand.color} strokeWidth={brand.strokeWidth} dot={{
                r: 5,
                fill: brand.color
              }} activeDot={{
                r: 7
              }} hide={hiddenBrands.includes(brand.name)} data-id="element-1413" />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Seção 2: Evolução por Atributo */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8" data-id="element-1414">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1415">
            Evolução por Atributo
          </h2>

          {/* Dropdown de Atributos */}
          <div className="mb-6" data-id="element-1416">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1417">
              Selecione o Atributo
            </label>
            <div className="relative w-full md:w-96" data-id="element-1418">
              <select value={selectedAttribute} onChange={e => setSelectedAttribute(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none bg-white pr-10 text-gray-900" data-id="element-1419">
                {ATTRIBUTES.map(attr => <option key={attr} value={attr} data-id="element-1420">
                    {attr}
                  </option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} data-id="element-1421" />
            </div>
          </div>

          {/* Gráfico de Atributo */}
          <div className="w-full h-[500px]" data-id="element-1422">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1423">
              <LineChart data={filteredAttributeRankings} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60
            }} data-id="element-1424">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-1425" />
                <XAxis dataKey="week" angle={-45} textAnchor="end" height={80} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} data-id="element-1426" />
                <YAxis reversed domain={[1, 6]} ticks={[1, 2, 3, 4, 5, 6]} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} label={{
                value: 'Posição no Ranking',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#6b7280',
                  fontSize: 14
                }
              }} data-id="element-1427" />
                <Tooltip content={<CustomTooltip data-id="element-1429" />} data-id="element-1428" />
                <Legend content={<CustomLegend data-id="element-1431" />} data-id="element-1430" />
                {BRANDS.map(brand => <Line key={brand.name} type="monotone" dataKey={`${brand.name}_position`} name={brand.name} stroke={brand.color} strokeWidth={brand.strokeWidth} dot={{
                r: 5,
                fill: brand.color
              }} activeDot={{
                r: 7
              }} hide={hiddenBrands.includes(brand.name)} data-id="element-1432" />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Seção 3: Ver análise específica */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1433">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1434">
            Ver Análise Específica
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-1435">
            Selecione uma semana para visualizar os gráficos detalhados da
            análise
          </p>

          {/* Seletor de Semana */}
          <div className="mb-6" data-id="element-1436">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1437">
              Escolha a Semana
            </label>
            <div className="relative w-full md:w-64" data-id="element-1438">
              <select value={selectedWeek} onChange={e => setSelectedWeek(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none bg-white pr-10 text-gray-900" data-id="element-1439">
                <option value="" data-id="element-1440">Selecione uma semana</option>
                {mockData.map(week => <option key={week.week} value={week.week} data-id="element-1441">
                    {week.week} (
                    {new Date(week.date).toLocaleDateString('pt-BR')})
                  </option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} data-id="element-1442" />
            </div>
          </div>

          {/* Gráficos da análise específica */}
          {weekData && <div className="space-y-12 mt-8" data-id="element-1443">
              {/* Score por Marca */}
              <div data-id="element-1444">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1445">
                  Score por Marca - {selectedWeek}
                </h3>
                <ScoreBarChart scores={weekData.scoreGeral} mainBrand="Conversion" findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1446" />
              </div>

              {/* Radar Comparativo */}
              <div data-id="element-1447">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1448">
                  Radar Comparativo - {selectedWeek}
                </h3>
                <RadarChart scores={weekData.scores} mainBrand="Conversion" attributes={ATTRIBUTES} allowMainBrandDeselection={true} data-id="element-1449" />
              </div>

              {/* Top Marcas por Atributo */}
              <div data-id="element-1450">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1451">
                  Top Marcas por Atributo - {selectedWeek}
                </h3>
                <AttributeTopBrands scores={weekData.scores} attributes={ATTRIBUTES} findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1452" />
              </div>

              {/* Heatmap de Scores */}
              <div data-id="element-1453">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1454">
                  Heatmap de Scores - {selectedWeek}
                </h3>
                <ScoreHeatmap scores={weekData.scores} scoreGeral={weekData.scoreGeral} mainBrand="Conversion" attributes={ATTRIBUTES} findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1455" />
              </div>

              {/* Justificativas */}
              <div data-id="element-1456">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1457">
                  Justificativas das Notas - {selectedWeek}
                </h3>
                <JustificativasSection scores={weekData.scores} attributes={ATTRIBUTES} mainBrand="Conversion" findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1458" />
              </div>
            </div>}

          {!selectedWeek && <div className="text-center py-12 text-gray-500" data-id="element-1459">
              Selecione uma semana para visualizar os gráficos detalhados
            </div>}
        </div>
      </div>
    </div>;
};
export default TesteSerieTemporal;